import * as React from 'react';

import Button from 'components/shared/Button';

const { useState, useMemo, useEffect } = React;

export type ImageGalleryImage = {
  id: string | number;
  src?: string | null;
};

export type ImageGalleryProps = {
  images: ImageGalleryImage[];
  setIsOpen: (isOpen: boolean) => void;
  startIndex: number;
};

function ImageGallery({ images, setIsOpen, startIndex }: ImageGalleryProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(startIndex);
  const imageIds = useMemo(() => images.map((image) => image.id), [images]);
  const pages = useMemo(() => images.length, [images]);
  const page = useMemo(() => activeImageIndex + 1, [activeImageIndex, imageIds]);

  const imgs = useMemo(() => {
    return images.map((image) => (
      <img src={image.src || '/assets/loading.gif'} key={image.id} alt="Preview" />
    ));
  }, [images]);

  function pageUp() {
    if (activeImageIndex + 1 === images.length) {
      setActiveImageIndex(0);
    } else {
      setActiveImageIndex(activeImageIndex + 1);
    }
  }

  function pageDown() {
    if (activeImageIndex === 0) {
      setActiveImageIndex(images.length - 1);
    } else {
      setActiveImageIndex(activeImageIndex - 1);
    }
  }

  function handleCloseGallery(e: MouseEvent) {
    const target = e.target as HTMLElement;

    if (target && target.className === 'gallery-container') {
      setIsOpen(false);
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleCloseGallery);

    return function cleanup() {
      document.removeEventListener('click', handleCloseGallery);
    };
  }, []);

  const isPaged = pages > 1;

  return (
    <div role="dialog" className="gallery-container" aria-label="Gallery of your images">
      {isPaged && (
        <Button
          type="icon"
          icon="caret-left"
          appearance="light"
          size="3x"
          a11yText="Show Previous Image"
          onClick={pageDown}
        />
      )}
      <div className="image-container">
        <Button
          type="icon"
          icon="times"
          size="2x"
          appearance="light"
          a11yText="Close Gallery"
          onClick={() => setIsOpen(false)}
        />
        {imgs[activeImageIndex]}
        {isPaged && (
          <p className="page-label">
            {page} of {pages}
          </p>
        )}
      </div>
      {isPaged && (
        <Button
          type="icon"
          icon="caret-right"
          appearance="light"
          size="3x"
          a11yText="Show Next Image"
          onClick={pageUp}
        />
      )}
    </div>
  );
}

export default ImageGallery;
